/**
 * Textos do widget, embutidos no bundle.
 *
 * O widget fala a língua de quem reporta, não a do dono da conta: quem está na
 * tela pode ser um cliente final em outro país. A escolha vem da configuração
 * do projeto e, quando ela é `auto`, do navegador de quem acessa.
 */

import type { WidgetLocale } from './types';

export type Language = 'pt-BR' | 'en';

const MESSAGES = {
  'pt-BR': {
    launcher: 'Feedback',
    title: 'Reportar um problema',
    stepTitle: 'O que aconteceu?',
    subtitle: 'Conte o que aconteceu. Enviamos junto a tela e os detalhes técnicos.',
    categoryLabel: 'Qual é o tipo?',
    hintBug: 'Algo quebrou ou não respondeu',
    hintUi: 'Está fora do lugar ou confuso',
    hintData: 'Um valor ou texto não bate',
    hintIdea: 'Uma ideia para melhorar',
    skipPoint: 'Descrever sem apontar',
    poweredBy: 'Feedback com Clixy',
    changeElement: 'Trocar elemento',
    goesWith: 'Vai junto, sem você digitar',
    withScreenshot: 'Captura da tela',
    withConsole: 'Erros da página',
    withNetwork: 'Requisições que falharam',
    withEnvironment: 'Navegador e tamanho da tela',
    descriptionLabel: 'Conte o que aconteceu',
    descriptionPlaceholder: 'Ex.: cliquei em finalizar compra e nada aconteceu.',
    emailLabel: 'Seu e-mail',
    emailPlaceholder: 'para respondermos, se precisar',
    pointAction: 'Apontar na tela',
    pointing: 'Clique no elemento com problema',
    pointed: 'Elemento marcado',
    removePoint: 'Remover marcação',
    capturing: 'Capturando a tela',
    send: 'Enviar',
    sending: 'Enviando',
    close: 'Fechar',
    successTitle: 'Recebido, obrigado',
    successBody: 'O time já tem o que precisa para investigar. Você não precisa explicar de novo.',
    sendAnother: 'Enviar outro',
    errorTitle: 'Não foi possível enviar',
    errorRetry: 'Tentar de novo',
    descriptionTooShort: 'Escreva um pouco mais para o time entender.',
    emailInvalid: 'Confira o endereço de e-mail.',
    categoryRequired: 'Escolha o tipo do problema.',
    includes: 'Vai junto: a página atual, o navegador e os erros recentes.',
  },
  en: {
    launcher: 'Feedback',
    title: 'Report a problem',
    stepTitle: 'What happened?',
    subtitle: 'Tell us what happened. We attach the screen and the technical details.',
    categoryLabel: 'What kind of issue?',
    hintBug: 'Something broke or did not respond',
    hintUi: 'It looks out of place or confusing',
    hintData: 'A value or a piece of text does not match',
    hintIdea: 'An idea to make it better',
    skipPoint: 'Describe without pointing',
    poweredBy: 'Feedback with Clixy',
    changeElement: 'Change element',
    goesWith: 'This goes along, without you typing it',
    withScreenshot: 'Screen capture',
    withConsole: 'Page errors',
    withNetwork: 'Requests that failed',
    withEnvironment: 'Browser and screen size',
    descriptionLabel: 'Tell us what happened',
    descriptionPlaceholder: 'For example: I clicked checkout and nothing happened.',
    emailLabel: 'Your e-mail',
    emailPlaceholder: 'so we can reply, if needed',
    pointAction: 'Point on screen',
    pointing: 'Click the element with the problem',
    pointed: 'Element marked',
    removePoint: 'Remove marker',
    capturing: 'Capturing the screen',
    send: 'Send',
    sending: 'Sending',
    close: 'Close',
    successTitle: 'Received, thank you',
    successBody: 'The team already has what it needs to investigate. You do not have to explain it again.',
    sendAnother: 'Send another',
    errorTitle: 'We could not send it',
    errorRetry: 'Try again',
    descriptionTooShort: 'Write a bit more so the team can understand.',
    emailInvalid: 'Check the e-mail address.',
    categoryRequired: 'Pick the kind of issue.',
    includes: 'Included: current page, browser and recent errors.',
  },
} as const;

export type MessageKey = keyof (typeof MESSAGES)['pt-BR'];

export function resolveLanguage(configured: WidgetLocale): Language {
  if (configured !== 'auto') return configured;

  const preferred = navigator.language?.toLowerCase() ?? '';
  return preferred.startsWith('pt') ? 'pt-BR' : 'en';
}

export function createTranslator(language: Language) {
  return (key: MessageKey): string => MESSAGES[language][key];
}

const CATEGORY_HINTS: Record<string, MessageKey> = {
  bug: 'hintBug',
  ui: 'hintUi',
  data: 'hintData',
  idea: 'hintIdea',
};

/** Descrição da categoria padrão. Categoria criada pelo cliente não tem uma. */
export function categoryHint(language: Language, categoryId: string): string | null {
  const key = CATEGORY_HINTS[categoryId];
  if (!key) return null;
  return MESSAGES[language][key];
}
